import type { ReactNode } from "react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isHighlightedField, useAuditHighlight } from "@/lib/auditHighlight";
import { cn } from "@/lib/utils";

/** A dynamic list of string inputs (e.g. multiple phone numbers / emails). */
export function StringListField({
  label,
  values,
  onChange,
  placeholder,
  addLabel,
  className,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  className?: string;
}) {
  const setAt = (index: number, value: string) => {
    const next = [...values];
    next[index] = value;
    onChange(next);
  };
  const removeAt = (index: number) => onChange(values.filter((_, i) => i !== index));
  const add = () => onChange([...values, ""]);
  return (
    <div className={cn("space-y-2", className)}>
      <Label>{label}</Label>
      <div className="space-y-1.5">
        {values.map((value, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <Input
              value={value}
              placeholder={placeholder}
              onChange={(e) => setAt(index, e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title="Remove"
              onClick={() => removeAt(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="h-4 w-4" />
          {addLabel ?? "Add"}
        </Button>
      </div>
    </div>
  );
}

/**
 * A labelled read-only value row for detail dialogs.
 *
 * Pass `field` (the DB column name) to opt the row into audit deep-link
 * highlighting: when the page was opened from an audit link that changed this
 * field, the row is drawn with a thick yellow border and a "changed" badge.
 */
export function Field({
  label,
  value,
  field,
  className,
}: {
  label: string;
  value?: ReactNode;
  /** The DB column name this row displays (for audit deep-link highlighting). */
  field?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const highlight = useAuditHighlight();
  const changed = isHighlightedField(highlight, field);
  return (
    <div className={cn("space-y-1", changed && "audit-field-highlight", className)}>
      <Label className="text-xs text-muted-foreground">
        {label}
        {changed ? (
          <span className="ms-1.5 rounded-full border border-[var(--color-audit-highlight)] px-1.5 text-[10px] font-semibold uppercase tracking-wide">
            {t("admin.auditChanged")}
          </span>
        ) : null}
      </Label>
      <div className="text-sm">{value ?? "—"}</div>
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} />
      {label}
    </label>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder ?? label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
